import { create } from 'apisauce';

import { apiMethods, Config } from '@nw/shared/config';

import {
  CancelOrderRequestType,
  CancelOrderResponseType,
  CreateChangeProgramOrderRequestType,
  CreateChangeProgramOrderResponseType,
  CreateNegativeRewardsAndBonusesRequestType,
  CreateNegativeRewardsAndBonusesResponseType,
  CreateUserRewardRequestType,
  CreateUserRewardResponseType,
  GetAffiliateCustomersRewardsRequestType,
  GetAffiliateCustomersRewardsResponseType,
  GetBackendSegmentEventRequestType,
  GetChangeProgramProductDetailsRequestType,
  GetChangeProgramReferralDiscountRequestType,
  GetCreateFreeOrderRequestType,
  GetCreateFreeOrderResponseType,
  GetCreatePaymentOrderRequestType,
  GetCreatePaymentOrderResponseType,
  GetInstallmentOrderDetailsRequestType,
  GetInstallmentOrderDetailsResponseType,
  GetLatestInProgressOrderResponseType,
  GetOrderDetailsBulkRequestType,
  GetOrderDetailsBulkResponseType,
  GetOrderDetailsRequestType,
  GetOrderDetailsResponseType,
  GetOrderStatusRequestType,
  GetOrderStatusResponseType,
  GetOrderUpdateUserExtraInfoRequestType,
  GetPaymentServicesResponseType,
  GetPositiveRewardsForNegativeRewardsRequestType,
  GetPositiveRewardsForNegativeRewardsResponseType,
  GetProductDetailsRequestType,
  GetProductDetailsResponseType,
  GetProductsPurchaseDetailsRequestType,
  GetProductsPurchaseDetailsResponseType,
  GetReferralCodeResponseType,
  GetReferralCodesResponseType,
  UserReferralTrackRequestType,
  UserReferralTrackResponseType,
  GetUserOrderBillingDetailsRequestType,
  GetUserOrderBillingDetailsResponseType,
  GetUserOrderDetailsRequestType,
  GetUserOrderDetailsResponseType,
  GetUserOrderEmiInstallmentDetailsRequestType,
  GetUserOrderEmiInstallmentDetailsResponseType,
  GetUserOrderEmiPriceDetailsRequestType,
  GetUserOrderEmiPriceDetailsResponseType,
  GetUserOrderReferralDiscountRequestType,
  GetUserOrderReferralDiscountResponseType,
  GetUserOrdersDetailsRequestType,
  GetUserOrdersDetailsResponseType,
  GetUserOrdersDetailsWithFiltersResponseType,
  GetUserOrdersWithStatusesRequestType,
  GetUserOrdersWithStatusesResponseType,
  GetUserReferralIdsRequestType,
  GetUserReferralIdsResponseType,
  GetUserRewardsRequestType,
  GetUserRewardsResponseType,
  GetUsersRegisteredProductsRequestType,
  GetUsersRegisteredProductsResponseType,
  GetUsersRewardsRequestType,
  GetUsersRewardsResponseType,
  GetUserSubscriptionsRequestType,
  GetUserSubscriptionsResponseType,
  GetUserTagsRequestType,
  GetUserTagsResponseType,
  GetUserWiseProductCountRequestType,
  GetUserWiseProductCountResponseType,
  OrderFullRefundRequestType,
  OrderFullRefundResponseType,
  UpdateChangeProgramOrderRequestType,
  UpdateChangeProgramOrderResponseType,
  UpdateRewardsStatusesRequestType,
  UpdateRewardsStatusesResponseType,
  UpdateUserProductUsageDetailsRequestType,
  UpdateUserProductUsageDetailsResponseType
} from './types';

import { PaymentServiceTypes } from '.';

class PaymentServiceAPI implements PaymentServiceTypes {
  api!: Record<string, any>;
  networkCallWithApisauce: any;

  constructor(networkCallWithApisauce) {
    this.api = create({
      baseURL: `${Config['PAYMENT_URL']}`
    });
    this.networkCallWithApisauce = networkCallWithApisauce;
  }

  getSelectedProductInfo({
    productCode,
    requestObject
  }: {
    productCode: string;
    requestObject: GetProductDetailsRequestType;
  }): Promise<GetProductDetailsResponseType> {
    return this.networkCallWithApisauce(
      this.api,
      `product_details/${productCode}/v1/`,
      requestObject,
      apiMethods.post
    );
  }

  getPaymentGateways(
    productCode: string
  ): Promise<GetPaymentServicesResponseType> {
    return this.networkCallWithApisauce(
      this.api,
      `products/${productCode}/payment_services/v1/`,
      {},
      apiMethods.get
    );
  }

  createPaymentOrderAndGetDetails(
    requestObject: GetCreatePaymentOrderRequestType
  ): Promise<GetCreatePaymentOrderResponseType> {
    return this.networkCallWithApisauce(
      this.api,
      'razorpay/order/create/v1/',
      requestObject,
      apiMethods.post
    );
  }

  createZeroAmountOrder(
    requestObject: GetCreateFreeOrderRequestType
  ): Promise<GetCreateFreeOrderResponseType> {
    return this.networkCallWithApisauce(
      this.api,
      'orders/create/free/v1/',
      requestObject,
      apiMethods.post
    );
  }

  checkReferralCode(
    requestObject: GetUserOrderReferralDiscountRequestType
  ): Promise<GetUserOrderReferralDiscountResponseType> {
    return this.networkCallWithApisauce(
      this.api,
      'user/order/referral_discount/v1/',
      requestObject,
      apiMethods.post
    );
  }

  getOrderStatus(
    requestObject: GetOrderStatusRequestType
  ): Promise<GetOrderStatusResponseType> {
    return this.networkCallWithApisauce(
      this.api,
      'order/status/v2/',
      requestObject,
      apiMethods.post
    );
  }

  segmentEvent(
    requestObject: GetBackendSegmentEventRequestType
  ): Promise<Record<string, never>> {
    return this.networkCallWithApisauce(
      this.api,
      'event/segment/v1/',
      requestObject,
      apiMethods.post
    );
  }

  getReferralCodes({
    productCode
  }: {
    productCode: string;
  }): Promise<GetReferralCodesResponseType> {
    return this.networkCallWithApisauce(
      this.api,
      `products/${productCode}/referral_codes/v1/`,
      {},
      apiMethods.get
    );
  }

  getReferralCode({
    productCode
  }: {
    productCode: string;
  }): Promise<GetReferralCodeResponseType> {
    return this.networkCallWithApisauce(
      this.api,
      `product/${productCode}/referral_code/v1/`,
      {},
      apiMethods.get
    );
  }

  trackReferralAttempt(
    requestObject: UserReferralTrackRequestType
  ): Promise<UserReferralTrackResponseType> {
    return this.networkCallWithApisauce(
      this.api,
      'user/referral/track/v1/',
      requestObject,
      apiMethods.post
    );
  }

  updateUserExtraInfo(
    requestObject: GetOrderUpdateUserExtraInfoRequestType
  ): Promise<Record<string, never>> {
    return this.networkCallWithApisauce(
      this.api,
      'order/update/user_extra_info/v1/',
      requestObject,
      apiMethods.post
    );
  }

  getUserOrdersDetails(): Promise<GetUserOrdersDetailsResponseType> {
    return this.networkCallWithApisauce(
      this.api,
      'user/orders/v1/',
      {},
      apiMethods.get
    );
  }

  getInstallmentOrderDetails(
    requestObject: GetInstallmentOrderDetailsRequestType
  ): Promise<GetInstallmentOrderDetailsResponseType> {
    return this.networkCallWithApisauce(
      this.api,
      'installment_orders/v1/',
      requestObject,
      apiMethods.post
    );
  }

  getProductsPurchaseDetails(
    requestObject: GetProductsPurchaseDetailsRequestType
  ): Promise<GetProductsPurchaseDetailsResponseType> {
    return this.networkCallWithApisauce(
      this.api,
      'products/purchase/v1/',
      requestObject,
      apiMethods.post
    );
  }

  getUserOrdersDetailsWithFilters(
    requestObject: GetUserOrdersDetailsRequestType
  ): Promise<GetUserOrdersDetailsWithFiltersResponseType> {
    return this.networkCallWithApisauce(
      this.api,
      'user_orders/details/v1/',
      requestObject,
      apiMethods.post
    );
  }

  cancelOrder(
    requestObject: CancelOrderRequestType
  ): Promise<CancelOrderResponseType> {
    return this.networkCallWithApisauce(
      this.api,
      'razorpay/order/cancel/v1/',
      requestObject,
      apiMethods.post
    );
  }

  getUserRewards(
    requestObject: GetUserRewardsRequestType
  ): Promise<GetUserRewardsResponseType> {
    return this.networkCallWithApisauce(
      this.api,
      'user/rewards/v1/',
      requestObject,
      apiMethods.post
    );
  }

  getUserReferralIds(
    requestObject: GetUserReferralIdsRequestType
  ): Promise<GetUserReferralIdsResponseType> {
    return this.networkCallWithApisauce(
      this.api,
      'user/referral_ids/v1/',
      requestObject,
      apiMethods.post
    );
  }

  getUserSubscriptions(
    requestObject: GetUserSubscriptionsRequestType
  ): Promise<GetUserSubscriptionsResponseType> {
    return this.networkCallWithApisauce(
      this.api,
      'subscriptions/get_user_subscriptions/v1/',
      requestObject,
      apiMethods.post
    );
  }

  orderFullRefund(
    requestObject: OrderFullRefundRequestType
  ): Promise<OrderFullRefundResponseType> {
    return this.networkCallWithApisauce(
      this.api,
      'order/full_refund/v2/',
      requestObject,
      apiMethods.post
    );
  }

  getLatestInProgressOrder(): Promise<GetLatestInProgressOrderResponseType> {
    return this.networkCallWithApisauce(
      this.api,
      'razorpay/order/latest/in_progress/v2/',
      {},
      apiMethods.get
    );
  }

  getOrderDetails({
    orderId,
    requestObject
  }: {
    orderId: string;
    requestObject: GetOrderDetailsRequestType;
  }): Promise<GetOrderDetailsResponseType> {
    return this.networkCallWithApisauce(
      this.api,
      `orders/${orderId}/v1/`,
      requestObject,
      apiMethods.post
    );
  }

  getUserTags(
    requestObject: GetUserTagsRequestType
  ): Promise<GetUserTagsResponseType> {
    return this.networkCallWithApisauce(
      this.api,
      'user/tags/v1/',
      requestObject,
      apiMethods.post
    );
  }

  getUserOrderEmiInstallmentDetails(
    requestObject: GetUserOrderEmiInstallmentDetailsRequestType
  ): Promise<GetUserOrderEmiInstallmentDetailsResponseType> {
    return this.networkCallWithApisauce(
      this.api,
      'user_order/emi_installment_details/v1/',
      requestObject,
      apiMethods.post
    );
  }

  getUserOrderEmiPriceDetails(
    requestObject: GetUserOrderEmiPriceDetailsRequestType
  ): Promise<GetUserOrderEmiPriceDetailsResponseType> {
    return this.networkCallWithApisauce(
      this.api,
      'user_order/emi/price_details/v1/',
      requestObject,
      apiMethods.post
    );
  }

  getUserOrderBillingDetails(
    requestObject: GetUserOrderBillingDetailsRequestType
  ): Promise<GetUserOrderBillingDetailsResponseType> {
    return this.networkCallWithApisauce(
      this.api,
      'user_order/billing_details/v1/',
      requestObject,
      apiMethods.post
    );
  }

  getUserOrderDetails(
    requestObject: GetUserOrderDetailsRequestType
  ): Promise<GetUserOrderDetailsResponseType> {
    return this.networkCallWithApisauce(
      this.api,
      'user_order/details/v1/',
      requestObject,
      apiMethods.post
    );
  }

  // Change Program APIs implementation
  createChangeProgramOrder(
    requestObject: CreateChangeProgramOrderRequestType
  ): Promise<CreateChangeProgramOrderResponseType> {
    return this.networkCallWithApisauce(
      this.api,
      'order/create/change_program/v1/',
      requestObject,
      apiMethods.post
    );
  }

  getChangeProgramReferralDiscount(
    requestObject: GetChangeProgramReferralDiscountRequestType
  ): Promise<GetUserOrderReferralDiscountResponseType> {
    return this.networkCallWithApisauce(
      this.api,
      'order/get/change_program/referral_discount/v1/',
      requestObject,
      apiMethods.post
    );
  }

  updateChangeProgramOrder(
    requestObject: UpdateChangeProgramOrderRequestType
  ): Promise<UpdateChangeProgramOrderResponseType> {
    return this.networkCallWithApisauce(
      this.api,
      'order/update/change_program/v1/',
      requestObject,
      apiMethods.post
    );
  }

  getChangeProgramProductDetails(
    requestObject: GetChangeProgramProductDetailsRequestType
  ): Promise<GetProductDetailsResponseType> {
    return this.networkCallWithApisauce(
      this.api,
      'order/get/change_program/product_details/v1/',
      requestObject,
      apiMethods.post
    );
  }

  // Additional missing APIs implementation
  getUserWiseProductCount(
    requestObject: GetUserWiseProductCountRequestType
  ): Promise<GetUserWiseProductCountResponseType> {
    return this.networkCallWithApisauce(
      this.api,
      'user/product/count/v1/',
      requestObject,
      apiMethods.post
    );
  }

  getUsersRewards(
    requestObject: GetUsersRewardsRequestType
  ): Promise<GetUsersRewardsResponseType> {
    return this.networkCallWithApisauce(
      this.api,
      'users/rewards/v1/',
      requestObject,
      apiMethods.post
    );
  }

  getAffiliateCustomersRewards(
    requestObject: GetAffiliateCustomersRewardsRequestType
  ): Promise<GetAffiliateCustomersRewardsResponseType> {
    return this.networkCallWithApisauce(
      this.api,
      'users/get_affiliate_customers_rewards/v1/',
      requestObject,
      apiMethods.post
    );
  }

  createUserReward(
    requestObject: CreateUserRewardRequestType
  ): Promise<CreateUserRewardResponseType> {
    return this.networkCallWithApisauce(
      this.api,
      'user_reward/create/v1/',
      requestObject,
      apiMethods.post
    );
  }

  getOrderDetailsBulk(
    requestObject: GetOrderDetailsBulkRequestType
  ): Promise<GetOrderDetailsBulkResponseType> {
    return this.networkCallWithApisauce(
      this.api,
      'order/bulk/v1/',
      requestObject,
      apiMethods.post
    );
  }

  getUserOrdersWithStatuses(
    requestObject: GetUserOrdersWithStatusesRequestType
  ): Promise<GetUserOrdersWithStatusesResponseType> {
    return this.networkCallWithApisauce(
      this.api,
      'get_user_orders/v1/',
      requestObject,
      apiMethods.post
    );
  }

  createNegativeRewardsAndBonuses(
    requestObject: CreateNegativeRewardsAndBonusesRequestType
  ): Promise<CreateNegativeRewardsAndBonusesResponseType> {
    return this.networkCallWithApisauce(
      this.api,
      'create_negative_rewards_and_bonuses/v1/',
      requestObject,
      apiMethods.post
    );
  }

  updateRewardsStatuses(
    requestObject: UpdateRewardsStatusesRequestType
  ): Promise<UpdateRewardsStatusesResponseType> {
    return this.networkCallWithApisauce(
      this.api,
      'update_rewards_statuses/v1/',
      requestObject,
      apiMethods.post
    );
  }

  getPositiveRewardsForNegativeRewards(
    requestObject: GetPositiveRewardsForNegativeRewardsRequestType
  ): Promise<GetPositiveRewardsForNegativeRewardsResponseType> {
    return this.networkCallWithApisauce(
      this.api,
      'get_positive_rewards_for_negative_rewards/v1/',
      requestObject,
      apiMethods.post
    );
  }

  getUsersRegisteredProducts(
    requestObject: GetUsersRegisteredProductsRequestType
  ): Promise<GetUsersRegisteredProductsResponseType> {
    return this.networkCallWithApisauce(
      this.api,
      'users/registered_products/v1/',
      requestObject,
      apiMethods.post
    );
  }

  updateUserProductUsageDetails(
    requestObject: UpdateUserProductUsageDetailsRequestType
  ): Promise<UpdateUserProductUsageDetailsResponseType> {
    return this.networkCallWithApisauce(
      this.api,
      'user/product_usage_details/v1/',
      requestObject,
      apiMethods.post
    );
  }
}
export default PaymentServiceAPI;
